import { Box, Stack, Typography, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { PercentageChart } from './chartBar/percentageChart/PercentageChart';

const getBarKey = (name: string, index: number) => `${name}-${index}`;

type AttendanceChartStatisticsCounts = {
  name: string;
  value: number;
};

export type AttendanceChartProps = {
  statistics: AttendanceChartStatisticsCounts[];
  primaryColor: string;
  isHovered: boolean;
  showChartBars: boolean;
  emptyBarTitles?: string[];
};

export const AttendanceChart: FC<AttendanceChartProps> = ({
  statistics,
  primaryColor,
  isHovered,
  showChartBars,
  emptyBarTitles,
}) => {
  const theme = useTheme();

  const defaultBar = getBarKey(statistics[0]?.name ?? '', 0);

  const [selectedBar, setSelectedBar] = useState<string>(defaultBar);

  const getBarColor = useCallback(
    (barKey: string) => {
      if (selectedBar === barKey) return primaryColor;
      if (isHovered) return theme.palette.common.light3;
      return theme.palette.common.light2;
    },
    [isHovered, primaryColor, selectedBar, theme],
  );

  const chartData = useMemo(() => {
    return statistics.map((s, i) => {
      const key = getBarKey(s.name, i);

      return {
        key,
        itemStyle: { color: getBarColor(key) },
        ...s,
      };
    });
  }, [statistics, getBarColor]);

  const mainValue = useMemo(() => {
    const mainValueCount = chartData.find((data) => data.key === selectedBar)?.value ?? 0;
    const [integerPart, decimalPart] = mainValueCount.toFixed(1).split('.');
    return { integerPart, decimalPart };
  }, [chartData, selectedBar]);

  const hasSingleBar = chartData.length === 1;

  useEffect(() => {
    setSelectedBar(defaultBar);
  }, [defaultBar]);

  if (!chartData.length) return null;

  return (
    <Stack gap={1} direction="row">
      <Box minWidth={65}>
        <Typography component="span" variant="h1" sx={{ color: primaryColor }}>
          {mainValue.integerPart}
        </Typography>
        <Typography component="span" variant="h3" sx={{ color: primaryColor }}>
          {`,${mainValue.decimalPart}%`}
        </Typography>
      </Box>

      {showChartBars && (
        <Stack width="100%" justifyContent="flex-end">
          {emptyBarTitles?.map((title) => (
            <Typography variant="caption" color="text.secondary" key={title}>
              {title}
            </Typography>
          ))}
          <Stack height={hasSingleBar ? 20 : 40}>
            <PercentageChart
              chartData={chartData}
              onBarOver={setSelectedBar}
              onBarOut={() => {
                setSelectedBar(defaultBar);
              }}
              gridOptions={{ top: hasSingleBar ? 1 : 6, bottom: 6 }}
              barOptions={{ barWidth: 12, cursor: 'default', barMinHeight: 8 }}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
